import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvUpdate, dvDelete } from '../api/dataverseClient';
import { TabKey } from '../views/project/ProjectNavBar';

// ── Per-project, per-surface visibility for the Stakeholder role ───────
//
// Stakeholders are often not M365 Group/Team members, so a PM controls
// which in-project tabs each stakeholder can see via this table. It is a
// UI-layer refinement ONLY — it can hide surfaces a Stakeholder could
// otherwise see, never grant access beyond their Dataverse Stakeholder
// role. PM/TM/Observer are unaffected (they keep the standard tab set).

export interface ProjectStakeholder {
  dagrun_pm_projectstakeholderid: string;
  dagrun_pm_name: string;
  dagrun_pm_personid: string | null;
  dagrun_pm_personname: string | null;
  dagrun_pm_personemail: string | null;
  dagrun_pm_canviewforms: boolean | null;
  dagrun_pm_canviewtasks: boolean | null;
  dagrun_pm_canviewdecisions: boolean | null;
  dagrun_pm_canviewissues: boolean | null;
  dagrun_pm_canviewrisks: boolean | null;
  dagrun_pm_canviewmilestones: boolean | null;
  dagrun_pm_canviewdocuments: boolean | null;
  _dagrun_pm_projectid_value?: string | null;
}

export interface StakeholderSurfaces {
  forms: boolean;
  tasks: boolean;
  decisions: boolean;
  issues: boolean;
  risks: boolean;
  milestones: boolean;
  documents: boolean;
}

// Each toggle maps to one in-project tab. Overview is the unavoidable
// baseline every stakeholder keeps (it can't be meaningfully hidden), so
// it is not part of the toggle set.
export const STAKEHOLDER_SURFACE_DEFS: { key: keyof StakeholderSurfaces; field: keyof ProjectStakeholder; tab: TabKey; label: string }[] = [
  { key: 'tasks',      field: 'dagrun_pm_canviewtasks',      tab: 'tasks',      label: 'Tasks' },
  { key: 'decisions',  field: 'dagrun_pm_canviewdecisions',  tab: 'decisions',  label: 'Decisions' },
  { key: 'issues',     field: 'dagrun_pm_canviewissues',     tab: 'issues',     label: 'Issues' },
  { key: 'risks',      field: 'dagrun_pm_canviewrisks',      tab: 'risks',      label: 'Risks' },
  { key: 'milestones', field: 'dagrun_pm_canviewmilestones', tab: 'milestones', label: 'Milestones' },
  { key: 'documents',  field: 'dagrun_pm_canviewdocuments',  tab: 'documents',  label: 'Documents' },
  { key: 'forms',      field: 'dagrun_pm_canviewforms',      tab: 'forms',      label: 'Forms' },
];

const FIELDS = [
  'dagrun_pm_projectstakeholderid', 'dagrun_pm_name',
  'dagrun_pm_personid', 'dagrun_pm_personname', 'dagrun_pm_personemail',
  'dagrun_pm_canviewforms', 'dagrun_pm_canviewtasks', 'dagrun_pm_canviewdecisions',
  'dagrun_pm_canviewissues', 'dagrun_pm_canviewrisks', 'dagrun_pm_canviewmilestones',
  'dagrun_pm_canviewdocuments', '_dagrun_pm_projectid_value',
];

export function surfacesOf(s: ProjectStakeholder): StakeholderSurfaces {
  return {
    forms: !!s.dagrun_pm_canviewforms,
    tasks: !!s.dagrun_pm_canviewtasks,
    decisions: !!s.dagrun_pm_canviewdecisions,
    issues: !!s.dagrun_pm_canviewissues,
    risks: !!s.dagrun_pm_canviewrisks,
    milestones: !!s.dagrun_pm_canviewmilestones,
    documents: !!s.dagrun_pm_canviewdocuments,
  };
}

/** Tab keys a stakeholder record may see — always includes 'overview'. */
export function allowedTabKeys(s: ProjectStakeholder): Set<TabKey> {
  const set = new Set<TabKey>(['overview']);
  for (const def of STAKEHOLDER_SURFACE_DEFS) {
    if (s[def.field]) set.add(def.tab);
  }
  return set;
}

function surfaceBody(surfaces: StakeholderSurfaces): Record<string, unknown> {
  return {
    dagrun_pm_canviewforms: surfaces.forms,
    dagrun_pm_canviewtasks: surfaces.tasks,
    dagrun_pm_canviewdecisions: surfaces.decisions,
    dagrun_pm_canviewissues: surfaces.issues,
    dagrun_pm_canviewrisks: surfaces.risks,
    dagrun_pm_canviewmilestones: surfaces.milestones,
    dagrun_pm_canviewdocuments: surfaces.documents,
  };
}

export async function getProjectStakeholders(pca: IPublicClientApplication, projectId: string): Promise<ProjectStakeholder[]> {
  return dvList<ProjectStakeholder>(pca, 'dagrun_pm_projectstakeholders', {
    select: FIELDS,
    filter: `_dagrun_pm_projectid_value eq ${projectId}`,
    orderby: 'dagrun_pm_personname asc',
    top: 500,
  });
}

/** The stakeholder record for a specific person on a project (nav gating). */
export async function getStakeholderRecord(
  pca: IPublicClientApplication, projectId: string, personId: string,
): Promise<ProjectStakeholder | null> {
  if (!personId) return null;
  const rows = await dvList<ProjectStakeholder>(pca, 'dagrun_pm_projectstakeholders', {
    select: FIELDS,
    filter: `_dagrun_pm_projectid_value eq ${projectId} and dagrun_pm_personid eq '${personId}'`,
    top: 1,
  });
  return rows[0] ?? null;
}

export async function createProjectStakeholder(
  pca: IPublicClientApplication,
  input: { projectId: string; personId: string; personName: string; personEmail?: string | null; surfaces: StakeholderSurfaces },
): Promise<string> {
  return dvCreate(pca, 'dagrun_pm_projectstakeholders', {
    dagrun_pm_name: input.personName.slice(0, 300),
    dagrun_pm_personid: input.personId,
    dagrun_pm_personname: input.personName,
    dagrun_pm_personemail: input.personEmail ?? null,
    ...surfaceBody(input.surfaces),
    'dagrun_pm_ProjectId@odata.bind': `/dagrun_pm_projects(${input.projectId})`,
  });
}

export async function updateStakeholderSurfaces(
  pca: IPublicClientApplication, id: string, surfaces: StakeholderSurfaces,
): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_projectstakeholders', id, surfaceBody(surfaces));
}

export async function deleteProjectStakeholder(pca: IPublicClientApplication, id: string): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_projectstakeholders', id);
}
